'use client';

import { atom, useAtomValue } from 'jotai';
import { Nav, NavProps } from '../Nav';
import { Container } from '../Container';
import { cn } from '../_lib/utils';
import { CtaAction } from '../CtaActions';
import { useEffect, useState } from 'react';
import { usePathname } from 'next/navigation';
import { Brands, Themes } from '../Theme';

export type HeaderProps = {
	navigation?: NavProps['nav'];
	ctaAction?: CtaAction;
	ctaActionLabel?: string;
	featuredText?: string;
	defaultBrand?: Brands;
	defaultTheme?: Themes;
};

export const showHeaderAtom = atom(true);

export function Header(props: HeaderProps) {
	const pathname = usePathname();
	const [theme, setTheme] = useState<Themes>(props.defaultTheme ?? 'dark');
	const showHeader = useAtomValue(showHeaderAtom);

	useEffect(() => {
		const observer = new IntersectionObserver(
			entries => {
				entries.forEach(entry => {
					if (entry.isIntersecting) {
						const newTheme = entry.target.getAttribute('data-theme');

						if (newTheme === 'light' || newTheme === 'dark') {
							setTheme(newTheme);
						}
					}
				});
			},
			{ rootMargin: '-10% 0px -89.9% 0px' },
		);

		const sections = document.querySelectorAll('[data-theme]:not([data-header-ignore])');
		sections.forEach(el => observer.observe(el));

		return () => {
			observer.disconnect();
		};
	}, [pathname]);

	return (
		<header
			className={cn(
				'absolute top-3 left-0 w-full max-w-dvw z-60 transition-opacity duration-normal md:top-6 lg:top-10',
				showHeader ? 'opacity-100 delay-700' : 'opacity-0 pointer-events-none [&_*]:pointer-events-none',
			)}
			data-theme={theme}
			data-header-ignore
		>
			<Container>
				<Nav
					defaultBrand={props.defaultBrand}
					nav={props.navigation ?? []}
					featuredText={props.featuredText}
					ctaNavButton={{
						ctaAction: props.ctaAction,
						label: props.ctaActionLabel,
						buttonProps: { styleType: 'primary-talk', styleSize: 'large' },
					}}
				/>
			</Container>
		</header>
	);
}
